import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { logout as apiLogout } from '../api';
import { useAuthStore } from '../store/auth';
import { productTokens } from '../theme';

const ShieldLogo = () => (
  <Box
    aria-hidden
    sx={{
      width: 34,
      height: 34,
      borderRadius: 1.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: alpha(productTokens.argus, 0.1),
      border: `1px solid ${alpha(productTokens.argus, 0.22)}`,
      flexShrink: 0,
    }}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 2L4.5 5.5V11C4.5 15.64 7.7 19.97 12 21C16.3 19.97 19.5 15.64 19.5 11V5.5L12 2Z"
        stroke={productTokens.argus}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

export default function NavBar() {
  const { isAuthed, username, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  const displayName = useMemo(() => (username || '').trim() || '成员', [username]);
  const avatarText = useMemo(() => displayName.slice(0, 1).toUpperCase(), [displayName]);

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch {
      // Local auth state should still clear if the server session already expired.
    } finally {
      clearAuth();
      navigate('/login');
    }
  };

  return (
    <AppBar position="sticky">
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ gap: 1.25, minWidth: 0 }}>
          <Box
            component={RouterLink}
            to={isAuthed ? '/dashboard' : '/login'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              minWidth: 0,
              mr: { xs: 1, md: 3 },
              color: 'text.primary',
              textDecoration: 'none',
              '&:focus-visible': {
                outline: `3px solid ${alpha(productTokens.argus, 0.22)}`,
                outlineOffset: 3,
                borderRadius: 2,
              },
            }}
          >
            <ShieldLogo />
            <Box sx={{ display: 'flex', minWidth: 0, flexDirection: 'column' }}>
              <Typography
                variant="h6"
                sx={{ color: 'primary.dark', fontWeight: 800, lineHeight: 1.05 }}
              >
                ARGUS
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  fontWeight: 650,
                  whiteSpace: 'nowrap',
                }}
              >
                认知筛查工作台
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {!isAuthed ? (
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {pathname.startsWith('/register') ? null : (
                <Button component={RouterLink} to="/login" variant="text">
                  账号登录
                </Button>
              )}
            </Box>
          ) : null}

          {isAuthed ? (
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.75, sm: 1.25 },
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <Button
                component={RouterLink}
                to="/dashboard"
                variant="text"
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: pathname.startsWith('/dashboard') ? 'primary.main' : 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                结果概览
              </Button>
              <Button
                component={RouterLink}
                to="/test"
                variant="contained"
                sx={{ px: { xs: 1.75, sm: 2.5 }, whiteSpace: 'nowrap' }}
              >
                开始测试
              </Button>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: { xs: 0.25, sm: 0.75 }, height: 24, alignSelf: 'center' }}
              />

              <Button
                variant="text"
                aria-label="打开账户菜单"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                  gap: 1.25,
                  px: { xs: 0.75, sm: 1.25 },
                  py: 0.75,
                  '&:hover': { bgcolor: alpha(productTokens.argus, 0.06) },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: alpha(productTokens.argus, 0.12),
                    color: 'primary.dark',
                    border: `1px solid ${alpha(productTokens.argus, 0.2)}`,
                    fontSize: 14,
                    fontWeight: 800,
                  }}
                >
                  {avatarText}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.primary', fontWeight: 800, lineHeight: 1.2 }}
                  >
                    {displayName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', lineHeight: 1 }}
                  >
                    已登录账号
                  </Typography>
                </Box>
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 180,
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    navigate('/dashboard');
                  }}
                  sx={{ py: 1.4, fontWeight: 650 }}
                >
                  结果概览
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    navigate('/test');
                  }}
                  sx={{ py: 1.4, fontWeight: 650 }}
                >
                  开始眼动测试
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    void handleLogout();
                  }}
                  sx={{ py: 1.4, fontWeight: 650, color: 'error.main' }}
                >
                  安全退出
                </MenuItem>
              </Menu>
            </Box>
          ) : null}
        </Toolbar>
      </Container>
    </AppBar>
  );
}
